import type { ComponentType, FunctionComponent, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Checkbox } from 'atoms/inputs/Checkbox';
import { Vertical } from 'atoms/layout/flex';
import { LazyMarkdown } from 'atoms/markdown/LazyMarkdown';
import { MarkdownDocumentStyleWrapper } from 'atoms/markdown/MarkdownDocumentStyleWrapper';
import { HeadlineM, HeadlineS } from 'atoms/typography';
import type { GraphQLResult, SubmitButtonProps } from 'components/form/Form';
import { GraphqlForm } from 'components/form/Form';
import { GAME_RULES } from 'constants/__generated__/routes';
import { useIntlContext } from 'contexts/intl';
import { useIsVisibleInViewport } from 'hooks/ui/useIsVisibleInViewport';
import { useFetch } from 'hooks/useFetch';
import { useToggle } from 'hooks/useToggle';
import { fileUrl } from 'lib/gitlab';
import { tabletAndAbove } from 'style/mediaQuery';

import { AcceptTermsAndPrivacyPolicyAndAgeLimit } from '../AcceptTermsAndPrivacyPolicyAndAgeLimit';
import { AgreeToReceiveOffersFromPartners } from '../AgreeToReceiveOffersFromPartners';
import { messages as sharedMessages } from '../messages';
import type { AcceptTermsArgs } from '../useAcceptTerms';

const messages = defineMessages({
  formTermsTitle: {
    id: 'PromptTermsDialog.formTermsTitle',
    defaultMessage:
      'You must accept the following to proceed with registration:',
  },
  termsDialogTitle: {
    id: 'PromptTermsDialog.termsDialogTitle',
    defaultMessage: 'Terms and Conditions',
  },
  gameRulesLabel: {
    id: 'PromptTermsDialog.gameRuleLabel',
    defaultMessage: 'I agree to the applicable <link>Game Rules</link>',
  },
  formPartnersTitle: {
    id: 'PromptTermsDialog.formPartnersTitle',
    defaultMessage:
      "Please indicate if you agree to share your information with Sorare's partners. This is optional.",
  },
  submit: {
    id: 'PromptTermsDialog.submit',
    defaultMessage: 'Accept',
  },
  scrollToBottom: {
    id: 'PromptTermsDialog.scrollToBottom',
    defaultMessage: 'Scroll to bottom',
  },
});

const TitleSection = styled(HeadlineM)`
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: -0.05em;
`;
const Scrollable = styled.div`
  overflow: auto;
  max-height: calc(100% - calc(8 * var(--unit)));
  padding: var(--double-unit);
`;
const Group = styled(Vertical).attrs({ gap: 2 })`
  margin-top: var(--double-unit);
`;
const MarkdownContainer = styled.div`
  min-height: 100vh;
  display: flex;
  @media ${tabletAndAbove} {
    min-height: calc(100% + var(--double-unit));
  }
`;
const Form = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;
`;

const LinkToGameRules = (s: ReactNode) => (
  <a href={GAME_RULES} target="_blank" rel="noreferrer">
    {s}
  </a>
);
export const Title = () => {
  const { formatMessage } = useIntlContext();
  return (
    <TitleSection as="h4">
      {formatMessage(messages.termsDialogTitle)}
    </TitleSection>
  );
};

const hasAcceptedMandatoryTerms = (acceptanceTerms: EffectiveAcceptTermsArgs) =>
  acceptanceTerms.acceptTerms &&
  acceptanceTerms.acceptAgeLimit &&
  acceptanceTerms.acceptPrivacyPolicy &&
  acceptanceTerms.acceptGameRules;

const Footer = styled(Vertical).attrs({ gap: 2 })`
  padding-top: var(--triple-unit);

  .isMobileWebviewSignUp & {
    border-right: 1px solid var(--c-nd-100);
    border-left: 1px solid var(--c-nd-100);
    border-top-left-radius: var(--quadruple-unit);
    border-top-right-radius: var(--quadruple-unit);
  }
`;

type EffectiveAcceptTermsArgs = Omit<AcceptTermsArgs, 'tcuToken'>;
type Props = {
  onFormSuccess: (
    acceptedTerms: EffectiveAcceptTermsArgs,
    result: GraphQLResult
  ) => void;
  acceptTerms: (args: EffectiveAcceptTermsArgs) => Promise<GraphQLResult>;
};

export const ReadThroughForm = ({ onFormSuccess, acceptTerms }: Props) => {
  const { formatMessage } = useIntlContext();
  const [error, setError] = useState<Error | null>(null);
  const [acceptTermsAndPrivacyPolicy, toggleAcceptTermsAndPrivacyPolicy] =
    useToggle(false);
  const [acceptGameRules, toggleAcceptGameRules] = useToggle(false);
  const [
    agreedToReceiveOffersFromPartners,
    toggleAgreedToReceiveOffersFromPartners,
  ] = useToggle(false);

  const {
    handleRef,
    node,
    isVisible: formIsVisible,
  } = useIsVisibleInViewport<HTMLDivElement>();

  const scrollToBottom = useCallback(() => {
    node?.scrollIntoView({ behavior: 'smooth' });
  }, [node]);

  const data = useFetch(
    fileUrl(
      formatMessage({
        id: 'Terms.path',
        defaultMessage: 'TERMS.md',
      })
    )
  );

  const onError = useCallback(
    (result: GraphQLResult) => {
      if (result?.errors?.length) {
        setError(new Error(formatMessage(sharedMessages.error)));
      }
    },
    [setError, formatMessage]
  );

  const termsInput = useMemo<EffectiveAcceptTermsArgs>(
    () => ({
      acceptTerms: acceptTermsAndPrivacyPolicy,
      acceptAgeLimit: acceptTermsAndPrivacyPolicy,
      acceptPrivacyPolicy: acceptTermsAndPrivacyPolicy,
      acceptGameRules,
      agreedToReceiveOffersFromPartners,
    }),
    [
      acceptGameRules,
      acceptTermsAndPrivacyPolicy,
      agreedToReceiveOffersFromPartners,
    ]
  );

  const doAcceptTerms = useCallback(
    (values: any, onResult: (result: GraphQLResult) => void) => {
      setError(null);
      if (hasAcceptedMandatoryTerms(termsInput)) {
        acceptTerms(termsInput).then(result => onResult(result));
      } else {
        onResult({
          error: formatMessage(sharedMessages.error),
          errors: [
            {
              message: formatMessage(sharedMessages.error),
            },
          ],
        });
      }
    },
    [acceptTerms, termsInput, formatMessage]
  );

  const onSuccess = useCallback(
    (result: GraphQLResult) => onFormSuccess(termsInput, result),
    [onFormSuccess, termsInput]
  );

  return (
    <Form
      onSubmit={doAcceptTerms}
      onSuccess={onSuccess}
      onError={onError}
      render={(
        Error: ComponentType<React.PropsWithChildren<unknown>>,
        SubmitButton: FunctionComponent<
          React.PropsWithChildren<SubmitButtonProps>
        >
      ) => (
        <>
          <Scrollable>
            <MarkdownContainer>
              <MarkdownDocumentStyleWrapper>
                <LazyMarkdown inModal data={data} skipHtml />
              </MarkdownDocumentStyleWrapper>
            </MarkdownContainer>
            <Group ref={handleRef}>
              <HeadlineS
                as="h5"
                style={error ? { color: 'var(--c-red-500)' } : {}}
              >
                <FormattedMessage {...messages.formTermsTitle} />
              </HeadlineS>
              <AcceptTermsAndPrivacyPolicyAndAgeLimit
                name="acceptTermsAndPrivacyPolicyLabel"
                checked={acceptTermsAndPrivacyPolicy}
                onChange={toggleAcceptTermsAndPrivacyPolicy}
              />
              <Checkbox
                name="acceptGameRules"
                checked={acceptGameRules}
                onChange={toggleAcceptGameRules}
                label={formatMessage(messages.gameRulesLabel, {
                  link: LinkToGameRules,
                })}
              />
            </Group>
            <Group>
              <HeadlineS as="h5">
                <FormattedMessage {...messages.formPartnersTitle} />
              </HeadlineS>
              <AgreeToReceiveOffersFromPartners
                name="agreedToReceiveOffersFromPartners"
                checked={agreedToReceiveOffersFromPartners}
                onChange={toggleAgreedToReceiveOffersFromPartners}
              />
            </Group>
          </Scrollable>
          <Footer>
            {formIsVisible !== undefined && (
              <>
                {!formIsVisible ? (
                  <Button
                    color="quaternary"
                    size="medium"
                    onClick={scrollToBottom}
                  >
                    <FormattedMessage {...messages.scrollToBottom} />
                  </Button>
                ) : (
                  <SubmitButton
                    size="medium"
                    disabled={!hasAcceptedMandatoryTerms(termsInput)}
                  >
                    <FormattedMessage {...messages.submit} />
                  </SubmitButton>
                )}
              </>
            )}
          </Footer>
        </>
      )}
    />
  );
};
